import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { ChevronDown, ChevronUp, ExternalLink, X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import AppBody from '../AppBody'
import { useActiveWeb3React } from '../../hooks'
import { useWalletModalToggle } from '../../state/application/hooks'
import { ButtonPrimary, ButtonSecondary } from '../../components/Button'
import { getContract, getEtherscanLink, isAddress } from '../../utils'
import { ChainId, ETHER } from '@uniswap/sdk'
import { useApproveCallback, ApprovalState } from '../../hooks/useApproveCallback'
import { TokenAmount, Token } from '@uniswap/sdk'
import { MaxUint256 } from '@ethersproject/constants'
import { useTokenAllowance } from '../../data/Allowances'
import { parseUnits, formatUnits } from '@ethersproject/units'
import StakeABI from '../../constants/abis/Stake.json'
import { useCurrencyBalance } from '../../state/wallet/hooks'
import { useAddPopup } from '../../state/application/hooks'
import Modal from '../../components/Modal'
import ROICalculatorModal from './ROICalculatorModal'
import StakeModal from './StakeModal'
import UnstakeModal from './UnstakeModal'
import { Pool } from './types'
import useAddTokenToMetamask from '../../hooks/useAddTokenToMetamask'

const STAKE_CONTRACT_ADDRESS = '0x2eCFed263B3e9697F398a2CC61910f8522d6b514'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 768px) {
    min-height: 90vh;
    margin-top: -3rem;
  }
`

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem;
  padding-top: 5rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 2rem 0;
    padding-top: 0;
  }
`

const HeaderSection = styled.div`
  margin-bottom: 2rem;
`

const Title = styled.h1`
  color: #e9ad21;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`

const Description = styled.p`
  color: #ffffff;
  font-size: 1rem;
  margin: 0 0 1.5rem 0;
  opacity: 0.8;
`

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const TabContainer = styled.div`
  display: flex;
  background-color: #2c2c2c;
  border-radius: 50px;
  overflow: hidden;
`

const TabButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  border: none;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ active }) => (active ? '#e9ad21' : 'transparent')};
  color: ${({ active }) => (active ? '#000000' : '#ffffff')};
  flex: 1;
  white-space: nowrap;

  &:hover {
    opacity: ${({ active }) => (active ? '1' : '0.8')};
  }
`

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const SearchLabel = styled.label`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  white-space: nowrap;
`

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  border: 1px solid #565656;
  background-color: #565656;
  color: #000000;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #e9ad21;
  }

  &::placeholder {
    color: #000000;
  }
`

const PoolList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: linear-gradient(to bottom, #000000, #231f20);
  box-shadow: 0 10px 9.4px -2px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
`

const PoolCard = styled.div<{ expanded: boolean }>`
  transition: all 0.2s;
  border-bottom: 1px solid #565656;
  &:last-child {
    border-bottom: none;
  }
`

const PoolAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #40444f;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.5rem;
  position: relative;
`

const TokenIcon = styled.img`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #131212;
  background-color: #131212;
`

const PoolInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 1 auto;
  overflow: hidden;
`

const PoolName = styled.div`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  width: 160px;
`

const PoolSubtitle = styled.div`
  color: #ffffff;
  font-size: 0.9rem;
`

const PoolMetrics = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
  padding: 2rem;
  align-items: flex-start;
  flex-wrap: nowrap;

  @media (max-width: 1300px) {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    padding: 2rem 1rem;
  }
`

const MetricItem = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: ${({ width }) => width || '130px'};
  // min-width: ${({ width }) => width || '130px'};
  // max-width: 340px;
  // flex: 0 1 auto;
`

const MetricLabel = styled.div`
  color: #e9ad21;
  font-size: 0.75rem;
  // white-space: nowrap;
`

const MetricValue = styled.div`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
`

const MetricValueToken = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  flex-direction: column;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;

  img {
    height: 16px;
    width: 16px;
  }
`

const ActionButton = styled(ButtonSecondary)`
  padding: 0.5rem 1rem;
  font-size: 1rem;
  width: auto;
  min-width: 80px;
  border: none;
  justify-content: flex-start;
  padding-right: 0;
  padding-left: 0;
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
  &:hover {
    border: none;
    outline: none;
    box-shadow: none;
  }
  &:active {
    border: none;
    outline: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0;
  }
`

const ExpandedContent = styled.div`
  padding: 2rem;
  border-top: 1px solid #40444f;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  background: #131212;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 2rem 1rem;
  }
`

const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: #ffffff;
  font-size: 0.95rem;
`

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const DetailLabel = styled.span`
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
`

const DetailValue = styled.span`
  color: #ffffff;
  font-weight: 700;
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  gap: 5px;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`

const ContractLink = styled.a`
  color: #e9ad21;
  text-decoration: none;
  display: flex;
  align-items: center;
  width: max-content;
  gap: 0.25rem;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`

const ContractRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;
`

const AddTokenButton = styled.button`
  background: none;
  border: none;
  color: #e9ad21;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  img {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    object-fit: contain;
  }
`

const StakingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  height: 100%;
  border: 1px solid #565656;
  border-radius: 10px;
  width: 100%;
  padding: 1.5rem;
`

const SectionLabel = styled.div`
  color: #e9ad21;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
  text-transform: uppercase;
`

const HarvestSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: flex-end;
  justify-content: space-between;
`

const EarnedInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
`

const StakingButton = styled(ButtonPrimary)`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border-radius: 10px;
  background-color: #e9ad21;
  color: #000000;
  font-weight: 700;
`

const EnableButton = styled(ButtonPrimary)`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  background-color: transparent;
  border: 1px solid #e9ad21;
  color: #e9ad21;
  border-radius: 10px;
  font-weight: 700;

  &:hover:not(:disabled) {
    background-color: #e9ad21;
    color: #000000;
  }

  &:focus:not(:disabled) {
    background-color: #e9ad21;
    color: #000000;
  }
`

const StakedAmountContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StakedAmountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StakedAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
`

// const StakedUSD = styled.div`
//   font-size: 0.85rem;
//   color: #b0b5c0;
// `

const StakedButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`

const StakedButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e9ad21;
  background-color: transparent;
  color: #e9ad21;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    border-color: #666;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const UnstakeTextButton = styled.button`
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e9ad21;
  background-color: transparent;
  color: #e9ad21;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0 16px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    background-color: #40444f;
    color: #6c7284;
    cursor: not-allowed;
    border-color: #40444f;
  }
`

const WarningModalContent = styled.div`
  padding: 2.25rem;
  background: linear-gradient(to bottom, #000000, #231f20);
  color: #ffffff;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  text-align: center;
  min-height: 360px;
`

const WarningHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

const WarningCloseButton = styled(X)`
  cursor: pointer;
  color: #ffffff;
  width: 28px;
  height: 28px;
  border: 1px solid #565656;
  border-radius: 50%;
  padding: 0.35rem;

  &:hover {
    color: #e9ad21;
  }
`

const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  text-align: center;
  padding: 3.5rem 0 5.5rem;
`

const WarningTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
`

const WarningDescription = styled.div`
  color: #d0d0d0;
  font-size: 0.95rem;
  line-height: 1.5;
`

const WarningActions = styled.div`
  width: 100%;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
`

const WarningPrimaryButton = styled(ButtonPrimary)`
  flex: 1;
  padding: 0.9rem 1rem;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px;
  background-color: #e9ad21;
  color: #000000;
`

const WarningSecondaryButton = styled.button`
  padding: 0.9rem 1rem;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px;
  background: transparent;
  color: #e9ad21;
  border: 1px solid #e9ad21;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(233, 173, 33, 0.08);
  }
`

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background-color: #231f20;
  color: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  z-index: 1000;
  pointer-events: none;
  line-height: 1.4;
  text-align: left;
  min-width: 180px;
  border: 1px solid #565656;

  /* 三角箭头 - 指向下方 */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 10px solid #231f20;
  }
`

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const CalculatorIconButton = styled.div`
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    cursor: pointer;
  }
`

// 模拟数据
// GraphQL 返回的数据类型
interface GraphQLPool {
  id: string
  endTime: string
  rewardPerSecond: string
  rewardForfeitedBeforeEnd?: boolean
  rewardToken: string
  rewardTokenDecimals: string
  rewardTokenName: string
  rewardTokenSymbol: string
  stakeToken: string
  stakeTokenDecimals: string
  stakeTokenName: string
  stakeTokenSymbol: string
  startTime: string
  totalReward: string
  totalStaked: string
  name?: string
  stakeTokenLogoURL?: string
  rewardTokenLogoURL?: string
  maxStakePerUser?: string
}

interface GraphQLResponse {
  data: {
    pools: GraphQLPool[]
  }
}

// 数值解析
function parseAmount(value: string | undefined, decimals: number): number {
  if (!value) return 0
  const num = parseFloat(value)
  if (Number.isNaN(num)) return 0
  return num / 10 ** decimals
}

// 获取 token 价格（带缓存）
async function fetchTokenPrice(address: string, cache: Map<string, number | null>): Promise<number | null> {
  if (!address) return null
  const key = address.toLowerCase()
  if (cache.has(key)) return cache.get(key) ?? null

  try {
    const res = await fetch(`https://graph.api.movachain.com/price/token?token_address=${address}`)
    if (!res.ok) {
      cache.set(key, null)
      return null
    }
    const data = await res.json()
    const price = data?.price ?? data?.data?.price ?? data?.usd ?? data?.data?.usd
    const parsed = price !== undefined ? Number(price) : NaN
    if (Number.isNaN(parsed)) {
      cache.set(key, null)
      return null
    }
    cache.set(key, parsed)
    return parsed
  } catch (error) {
    console.error('Failed to fetch price:', address, error)
    cache.set(key, null)
    return null
  }
}

// 快速获取基本池子数据（不包含价格和 APR 计算）
async function fetchPoolsBasic(): Promise<Pool[]> {
  const query = `
    query QueryPools {
      pools {
        endTime
        id
        rewardPerSecond
        rewardForfeitedBeforeEnd
        rewardToken
        rewardTokenDecimals
        rewardTokenName
        rewardTokenSymbol
        stakeToken
        stakeTokenDecimals
        stakeTokenName
        stakeTokenSymbol
        startTime
        totalReward
        totalStaked
        name
        stakeTokenLogoURL
        rewardTokenLogoURL
        maxStakePerUser
      }
    }
  `

  try {
    const response = await fetch('https://graph.api.movachain.com/subgraphs/name/usd1swap/usd1-stake-main', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: GraphQLResponse = await response.json()

    if (result.data && result.data.pools) {
      const pools = result.data.pools.map(pool => {
        const endTime = parseInt(pool.endTime) * 1000 // 转换为毫秒
        const startTime = parseInt(pool.startTime || '0') // 秒
        const now = Date.now()
        const isLive = endTime > now

        const rewardTokenDecimals = Number(pool.rewardTokenDecimals || '18')
        const stakeTokenDecimals = Number(pool.stakeTokenDecimals || '18')

        const rewardPerSecondToken = parseAmount(pool.rewardPerSecond, rewardTokenDecimals)
        const totalStakedTokens = parseAmount(pool.totalStaked, stakeTokenDecimals)

        const contractAddress = STAKE_CONTRACT_ADDRESS
        const stakingTokenAddress =
          pool.stakeToken && isAddress(pool.stakeToken) !== false ? (isAddress(pool.stakeToken) as string) ?? '' : ''
        const rewardTokenAddress =
          pool.rewardToken && isAddress(pool.rewardToken) !== false ? (isAddress(pool.rewardToken) as string) ?? '' : ''

        // 基本信息先返回，价格和 APR 稍后更新
        return {
          id: pool.id,
          name: pool.name || `Earn ${pool.rewardTokenSymbol}`,
          stakeToken: pool.stakeTokenSymbol,
          rewardToken: pool.rewardTokenSymbol,
          rewardTokenLogoURL: pool.rewardTokenLogoURL,
          stakeTokenLogoURL: pool.stakeTokenLogoURL,
          rewardTokenAddress,
          stakeTokenAddress: stakingTokenAddress,
          rewardTokenDecimals,
          stakeTokenDecimals,
          rewardPerSecond: rewardPerSecondToken.toString(),
          avatar: '',
          rewardForfeitedBeforeEnd: pool.rewardForfeitedBeforeEnd ?? false,
          apr: 0, // 占位符，稍后更新
          displayAprLabel: 'yieldFarming.tokenAprNoPrice',
          displayAprValue: '--', // 占位符
          rewardPrice: null, // 稍后更新
          stakePrice: null, // 稍后更新
          totalStaked: totalStakedTokens.toString(),
          maxStakePerUser:
            pool.maxStakePerUser && pool.maxStakePerUser !== '0'
              ? formatUnits(pool.maxStakePerUser, stakeTokenDecimals)
              : '0',
          startTime,
          endTime: endTime,
          contractAddress: contractAddress,
          isLive: isLive
        }
      })

      return pools
    }

    return []
  } catch (error) {
    console.error('Failed to fetch pools:', error)
    return []
  }
}

// 异步更新池子的价格和 APR 数据
async function updatePoolsWithPricesAndAPR(pools: Pool[]): Promise<Pool[]> {
  const priceCache = new Map<string, number | null>()
  const secondsPerYear = 365 * 24 * 60 * 60

  const updatedPools = await Promise.all(
    pools.map(async pool => {
      const rewardPrice = await fetchTokenPrice(pool.rewardTokenAddress, priceCache)
      const stakePrice = await fetchTokenPrice(pool.stakeTokenAddress, priceCache)

      const rewardPerSecondToken = parseFloat(pool.rewardPerSecond)
      const totalStakedTokens = parseFloat(pool.totalStaked)
      const annualRewardTokens = rewardPerSecondToken * secondsPerYear

      let apr = 0
      let displayAprLabel = 'yieldFarming.tokenAprNoPrice'
      let displayAprValue = '--'

      if (totalStakedTokens > 0) {
        if (rewardPrice !== null && stakePrice !== null && stakePrice > 0) {
          apr = ((annualRewardTokens * rewardPrice) / (totalStakedTokens * stakePrice)) * 100
          displayAprLabel = 'yieldFarming.apr'
          displayAprValue = `${apr.toFixed(2)}%`
        } else {
          const tokenApr = (annualRewardTokens / totalStakedTokens) * 100
          displayAprLabel = 'yieldFarming.tokenAprNoPrice'
          displayAprValue = tokenApr.toFixed(2) + '%'
          apr = tokenApr
        }
      }

      return {
        ...pool,
        apr,
        displayAprLabel,
        displayAprValue,
        rewardPrice,
        stakePrice
      }
    })
  )

  return updatedPools
}

// 兼容旧接口，返回完整数据（用于刷新等场景）
async function fetchPools(): Promise<Pool[]> {
  const basicPools = await fetchPoolsBasic()
  return updatePoolsWithPricesAndAPR(basicPools)
}

// 格式化倒计时
function formatTimeRemaining(endTime: number, t: (key: string) => string): string {
  const now = Date.now()
  const diff = endTime - now
  if (diff <= 0) return t('yieldFarming.ended')

  const days = Math.floor(diff / (24 * 60 * 60 * 1000))
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000))

  if (days > 0) {
    return (t as any)('yieldFarming.timeDays', { days: days.toString().padStart(2, '0') })
  } else if (hours > 0) {
    return (t as any)('yieldFarming.timeHoursMinutes', { hours, minutes })
  } else {
    return (t as any)('yieldFarming.timeMinutes', { minutes })
  }
}

// 格式化结束时间
function formatEndTime(endTime: number): string {
  const date = new Date(endTime)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  })
}

// 池子卡片组件
function PoolCardComponent({
  pool,
  expanded,
  onToggle,
  onPoolUpdate,
  userEarned = '0',
  onRefreshEarned
}: {
  pool: Pool
  expanded: boolean
  onToggle: () => void
  onPoolUpdate?: (poolId: string) => void
  userEarned?: string
  onRefreshEarned?: () => void
}) {
  const { t } = useTranslation()
  const { account, chainId, library } = useActiveWeb3React()
  const addPopup = useAddPopup()
  const [showTooltip, setShowTooltip] = useState(false)
  const [showROICalculator, setShowROICalculator] = useState(false)
  const [totalHarvested, setTotalHarvested] = useState('0')
  const [isHarvesting, setIsHarvesting] = useState(false)
  const toggleWalletModal = useWalletModalToggle()

  const timeRemaining = formatTimeRemaining(pool.endTime, t)
  const endTimeFormatted = formatEndTime(pool.endTime)

  // 获取钱包余额用于 ROI Calculator
  const isWMOVA = (pool.stakeToken || '').toUpperCase() === 'WMOVA'
  const isValidStakingTokenAddress = useMemo(() => {
    return pool.stakeTokenAddress && isAddress(pool.stakeTokenAddress) !== false
  }, [pool.stakeTokenAddress])

  const stakingToken = useMemo(() => {
    if (!chainId || !isValidStakingTokenAddress) return undefined
    try {
      const address = isAddress(pool.stakeTokenAddress)
      if (!address) return undefined
      const decimals = pool.stakeTokenDecimals || 18
      return new Token(chainId, address, decimals, pool.stakeToken, pool.stakeToken)
    } catch {
      return undefined
    }
  }, [chainId, pool.stakeTokenAddress, pool.stakeToken, pool.stakeTokenDecimals, isValidStakingTokenAddress])

  const rewardToken = useMemo(() => {
    if (!chainId || !pool.rewardTokenAddress || isAddress(pool.rewardTokenAddress) === false) return undefined
    try {
      const address = isAddress(pool.rewardTokenAddress)
      if (!address) return undefined
      const decimals = pool.rewardTokenDecimals ?? 18
      return new Token(chainId, address, decimals, pool.rewardToken, pool.rewardToken)
    } catch {
      return undefined
    }
  }, [chainId, pool.rewardTokenAddress, pool.rewardToken, pool.rewardTokenDecimals])

  const { addToken } = useAddTokenToMetamask(rewardToken, pool.rewardTokenLogoURL)

  const walletBalance = useCurrencyBalance(account ?? undefined, isWMOVA ? ETHER : stakingToken)

  // 从子图读取用户历史已收获总量
  const loadUserTotalHarvested = async () => {
    if (!account) {
      setTotalHarvested('0')
      return
    }

    try {
      const response = await fetch('https://graph.api.movachain.com/subgraphs/name/usd1swap/usd1-stake-main', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: 'query MyQuery($address: String!) { userStakes(where: {user: $address}) { pool{id} totalHarvested } }',
          variables: {
            address: account.toLowerCase()
          }
        })
      })

      const json = await response.json()
      const stakes = json?.data?.userStakes ?? []

      // 根据当前池子的 id 匹配对应的 totalHarvested
      const currentPoolStake = stakes.find((stake: any) => stake?.pool?.id === pool.id)

      if (currentPoolStake && currentPoolStake.totalHarvested) {
        // 使用 parseAmount 处理 totalHarvested
        const rewardTokenDecimals = pool.rewardTokenDecimals || 18
        const totalHarvestedAmount = parseAmount(currentPoolStake.totalHarvested, rewardTokenDecimals)
        setTotalHarvested(totalHarvestedAmount.toString())
      } else {
        setTotalHarvested('0')
      }
    } catch (error) {
      console.error('Failed to load user total harvested amount:', error)
      setTotalHarvested('0')
    }
  }

  useEffect(() => {
    if (account) {
      loadUserTotalHarvested()
    } else {
      setTotalHarvested('0')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, pool.id, pool.rewardTokenDecimals])

  const numericUserEarned = useMemo(() => {
    const v = parseFloat(userEarned || '0')
    return Number.isFinite(v) ? v : 0
  }, [userEarned])

  const numericTotalHarvested = useMemo(() => {
    const v = parseFloat(totalHarvested || '0')
    return Number.isFinite(v) ? v : 0
  }, [totalHarvested])

  const totalEarnings = useMemo(() => {
    return numericUserEarned + numericTotalHarvested
  }, [numericUserEarned, numericTotalHarvested])

  /* const totalEarningsUSD = useMemo(() => {
    if (!pool.rewardPrice) return null
    return totalEarnings * Number(pool.rewardPrice)
  }, [totalEarnings, pool.rewardPrice]) */

  const handleHarvest = async () => {
    if (!account || isHarvesting || numericUserEarned <= 0 || Date.now() <= pool.endTime) return

    try {
      setIsHarvesting(true)

      if (!library) return

      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)
      const pid = Number(pool.id)
      if (Number.isNaN(pid)) return

      const tx = await contract.harvest(pid)
      await tx.wait()

      await loadUserTotalHarvested()
      // 刷新批量待领取收益数据
      if (onRefreshEarned) {
        onRefreshEarned()
      }
      addPopup({
        message: {
          type: 'success',
          text: t('yieldFarming.harvested', { amount: numericUserEarned.toFixed(6), token: pool.rewardToken })
        }
      })
      // 刷新池子数据
      if (onPoolUpdate) {
        onPoolUpdate(pool.id)
      }
    } catch (error) {
      console.error('Failed to harvest:', error)
      // 不是用户手动取消的情况，提示错误
      if ((error as any).code !== 4001) {
        addPopup({ message: { type: 'error', text: t('yieldFarming.harvestFailed') + ': ' + (error as any).message } })
      }
    } finally {
      setIsHarvesting(false)
    }
  }

  return (
    <PoolCard expanded={expanded}>
      <PoolMetrics>
        <MetricItem width="220px">
          <PoolInfo>
            <PoolAvatar
              style={
                pool.rewardTokenLogoURL
                  ? {
                      backgroundImage: `url(${pool.rewardTokenLogoURL})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }
                  : undefined
              }
            >
              {(() => {
                const iconSrc =
                  pool.stakeTokenLogoURL ||
                  (pool.stakeToken && pool.stakeToken.toUpperCase().includes('MOVA')
                    ? '/images/yield_token_mova.png'
                    : undefined)
                return iconSrc ? <TokenIcon src={iconSrc} alt={pool.stakeToken} /> : null
              })()}
            </PoolAvatar>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <PoolName>{pool.name}</PoolName>
              <PoolSubtitle>
                {t('yieldFarming.stake')} {pool.stakeToken}
              </PoolSubtitle>
            </div>
          </PoolInfo>
        </MetricItem>
        <MetricItem width="160px">
          <MetricLabel>
            {t('yieldFarming.earningsHistory', {
              token: pool.rewardToken
            })}
          </MetricLabel>
          <MetricValue>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {(() => {
                const historyAmount = totalEarnings
                // const historyAmountUSD = totalEarningsUSD
                // 如果历史收益为 0 且还未加载，显示占位符
                if (historyAmount === 0 && !account) {
                  return '--'
                }
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span>
                      <span>{historyAmount.toFixed(6)}</span>
                      {/* {historyAmountUSD !== null && <span> ({historyAmountUSD.toFixed(6)} USD1)</span>} */}
                    </span>
                  </div>
                )
              })()}
            </div>
          </MetricValue>
        </MetricItem>
        <MetricItem width="160px">
          <MetricLabel>{t('yieldFarming.totalStaked')}</MetricLabel>
          <MetricValue>
            {(() => {
              const totalStakedNum = parseFloat(pool.totalStaked || '0')
              // 如果总质押量为 0 且价格数据还未加载，显示占位符避免误解
              // 如果价格数据已加载（即使为 null），说明数据已完整，显示实际值
              if (totalStakedNum === 0 && pool.rewardPrice === null && pool.stakePrice === null) {
                return '--'
              }
              // 如果值为无效，显示占位符
              if (!Number.isFinite(totalStakedNum)) {
                return '--'
              }

              if (totalStakedNum === 0) return '0'
              if (totalStakedNum < 0.0001) return '<0.0001'
              const decimals = Math.min(pool.stakeTokenDecimals || 18, 4)
              const factor = 10 ** decimals
              const floored = Math.floor(totalStakedNum * factor) / factor
              const formattedNumber = floored.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: decimals
              })
              return (
                <MetricValueToken>
                  <span>{formattedNumber}</span>
                  <span>{pool.stakeToken}</span>
                </MetricValueToken>
              )
            })()}
          </MetricValue>
        </MetricItem>
        <MetricItem width="200px">
          <MetricLabel>{t(pool.displayAprLabel)}</MetricLabel>
          <MetricValue>
            {(() => {
              // 如果 APR 值为 "--"（占位符），直接显示
              if (pool.displayAprValue === '--') {
                return '--'
              }

              // 判断是否显示 N/A（low TVL）
              const totalStakedNum = parseFloat(pool.totalStaked || '0')
              let shouldShowNA = false

              // 只有当价格数据已加载时才判断 low TVL
              if (pool.rewardPrice !== null && pool.rewardPrice !== undefined) {
                if (pool.rewardPrice > 0) {
                  // Token 有价格时，检查总质押是否低于 100 USD1
                  if (pool.stakePrice !== null && pool.stakePrice !== undefined && pool.stakePrice > 0) {
                    const totalStakedUSD = totalStakedNum * pool.stakePrice
                    shouldShowNA = totalStakedUSD < 100
                  }
                } else {
                  // Token 没有价值时，检查总质押是否低于 100 token
                  shouldShowNA = totalStakedNum < 100
                }
              }

              return shouldShowNA ? t('yieldFarming.aprLowTVL') : pool.displayAprValue
            })()}
            <CalculatorIconButton onClick={() => setShowROICalculator(true)}>
              <img src="/images/calculator.png" alt="calculator" />
            </CalculatorIconButton>
          </MetricValue>
        </MetricItem>
        <MetricItem width="100px">
          <MetricLabel>{t('yieldFarming.endsIn')}</MetricLabel>
          <TooltipWrapper onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
            <MetricValue>
              {timeRemaining}
              <IconWrapper>
                <img src="/images/clock.png" alt="clock" />
              </IconWrapper>
            </MetricValue>
            {showTooltip && (
              <Tooltip>
                <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{t('yieldFarming.endTime')}</div>
                <div>{endTimeFormatted}</div>
              </Tooltip>
            )}
          </TooltipWrapper>
        </MetricItem>

        <MetricItem width="100px" style={{ flex: '0 0 auto' }}>
          <ActionButton
            onClick={e => {
              e.stopPropagation()
              onToggle()
            }}
          >
            {expanded ? (
              <>
                {t('yieldFarming.hide')} <ChevronUp size={16} style={{ marginLeft: '0.25rem' }} />
              </>
            ) : (
              <>
                {t('yieldFarming.details')} <ChevronDown size={16} style={{ marginLeft: '0.25rem' }} />
              </>
            )}
          </ActionButton>
        </MetricItem>
      </PoolMetrics>

      {expanded && (
        <ExpandedContent>
          <DetailColumn>
            <DetailInfo>
              <DetailItem>
                <DetailLabel>{t(pool.displayAprLabel)}</DetailLabel>
                <DetailValue>
                  {pool.displayAprValue}
                  <CalculatorIconButton onClick={() => setShowROICalculator(true)}>
                    <img src="/images/calculator.png" alt="calculator" />
                  </CalculatorIconButton>
                </DetailValue>
              </DetailItem>
              {pool.maxStakePerUser && pool.maxStakePerUser !== '0' && (
                <DetailItem>
                  <DetailLabel>{t('yieldFarming.maxStakePerUser')}</DetailLabel>
                  <DetailValue>
                    {pool.maxStakePerUser} {pool.stakeToken}
                  </DetailValue>
                </DetailItem>
              )}
              <DetailItem>
                <DetailLabel>{t('yieldFarming.endsIn')}</DetailLabel>
                <DetailValue>
                  {timeRemaining}
                  <img src="/images/clock.png" alt="clock" />
                </DetailValue>
              </DetailItem>
            </DetailInfo>
            <ContractRow>
              <ContractLink
                href={getEtherscanLink(chainId || ChainId.MAINNET, pool.contractAddress, 'address')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('yieldFarming.viewContract')} <ExternalLink size={14} />
              </ContractLink>
              {rewardToken && (
                <AddTokenButton type="button" onClick={addToken}>
                  {t('yieldFarming.addTokenToWallet', { token: pool.rewardToken })}
                  {pool.rewardTokenLogoURL && <img src={pool.rewardTokenLogoURL} alt={pool.rewardToken} />}
                </AddTokenButton>
              )}
            </ContractRow>
          </DetailColumn>

          <DetailColumn>
            <StakingSection>
              <SectionLabel>{t('yieldFarming.earned', { token: pool.rewardToken })}</SectionLabel>
              <HarvestSection>
                <EarnedInput type="text" value={userEarned} readOnly disabled={!account} />
                <StakingButton
                  disabled={!account || numericUserEarned <= 0 || isHarvesting || Date.now() <= pool.endTime}
                  onClick={handleHarvest}
                >
                  {isHarvesting ? t('yieldFarming.harvesting') : t('yieldFarming.harvest')}
                </StakingButton>
              </HarvestSection>
            </StakingSection>
          </DetailColumn>

          <DetailColumn>
            <StakingSection>
              {account ? (
                <EnableStakeSection pool={pool} onPoolUpdate={onPoolUpdate} onRefreshEarned={onRefreshEarned} />
              ) : (
                <>
                  <SectionLabel>{t('yieldFarming.startStaking')}</SectionLabel>
                  <StakingButton onClick={toggleWalletModal}>{t('yieldFarming.connectWallet')}</StakingButton>
                </>
              )}
            </StakingSection>
          </DetailColumn>
        </ExpandedContent>
      )}

      <ROICalculatorModal
        isOpen={showROICalculator}
        onDismiss={() => setShowROICalculator(false)}
        apr={pool.apr}
        stakeToken={pool.stakeToken}
        stakePrice={pool.stakePrice}
        rewardToken={pool.rewardToken}
        rewardPrice={pool.rewardPrice}
        totalStaked={pool.totalStaked}
        rewardPerSecond={pool.rewardPerSecond}
        balance={walletBalance?.toExact() ?? '0'}
      />
    </PoolCard>
  )
}

// Enable/Stake 组件
function EnableStakeSection({
  pool,
  onPoolUpdate,
  onRefreshEarned
}: {
  pool: Pool
  onPoolUpdate?: (poolId: string) => void
  onRefreshEarned?: () => void
}) {
  const { t } = useTranslation()
  const { account, chainId, library } = useActiveWeb3React()
  const addPopup = useAddPopup()
  const [stakeAmount, setStakeAmount] = useState('')
  const [unstakeAmount, setUnstakeAmount] = useState('')
  const [isStaking, setIsStaking] = useState(false)
  const [isUnstaking, setIsUnstaking] = useState(false)
  const [showStakeModal, setShowStakeModal] = useState(false)
  const [showUnstakeModal, setShowUnstakeModal] = useState(false)
  const [showUnstakeConfirm, setShowUnstakeConfirm] = useState(false)
  const [userStakedAmount, setUserStakedAmount] = useState('0')

  const isWMOVA = (pool.stakeToken || '').toUpperCase() === 'WMOVA'

  // 验证地址是否有效
  const isValidContractAddress = useMemo(() => {
    return pool.contractAddress && isAddress(pool.contractAddress) !== false
  }, [pool.contractAddress])

  const isValidStakingTokenAddress = useMemo(() => {
    return pool.stakeTokenAddress && isAddress(pool.stakeTokenAddress) !== false
  }, [pool.stakeTokenAddress])

  // 创建 Token 对象用于授权
  const stakingToken = useMemo(() => {
    if (!chainId || !isValidStakingTokenAddress) return undefined
    try {
      const address = isAddress(pool.stakeTokenAddress)
      if (!address) return undefined
      const decimals = pool.stakeTokenDecimals || 18
      return new Token(chainId, address, decimals, pool.stakeToken, pool.stakeToken)
    } catch {
      return undefined
    }
  }, [chainId, pool.stakeTokenAddress, pool.stakeToken, pool.stakeTokenDecimals, isValidStakingTokenAddress])

  // 授权金额（使用最大金额）
  const approvalAmount = useMemo(() => {
    if (!stakingToken || isWMOVA) return undefined
    return new TokenAmount(stakingToken, MaxUint256.toString())
  }, [stakingToken, isWMOVA])

  // 检查授权状态（只在地址有效时调用）
  const contractAddress = isValidContractAddress ? (isAddress(pool.contractAddress) as string) : undefined
  const [approvalState, approveCallback] = useApproveCallback(approvalAmount, contractAddress)

  // 检查当前授权额度（只在地址有效时调用）
  const currentAllowance = useTokenAllowance(
    isWMOVA ? undefined : stakingToken,
    isWMOVA ? undefined : account ?? undefined,
    isWMOVA ? undefined : contractAddress
  )

  // 检查是否已授权
  // isWMOVA: 原生代币（WMOVA）不需要授权
  // approvalState === ApprovalState.APPROVED: 授权状态为已批准
  // currentAllowance && currentAllowance.greaterThan('0'): 当前授权额度大于0
  const isApproved =
    isWMOVA || approvalState === ApprovalState.APPROVED || (currentAllowance && currentAllowance.greaterThan('0'))

  const walletBalance = useCurrencyBalance(account ?? undefined, isWMOVA ? ETHER : stakingToken)
  const walletBalanceNumber = useMemo(() => {
    const v = parseFloat(walletBalance?.toExact() ?? '0')
    return Number.isFinite(v) ? v : 0
  }, [walletBalance])

  // 从合约读取用户已质押数量
  const loadUserStakedAmount = async () => {
    if (!account || !library) {
      setUserStakedAmount('0')
      return
    }

    try {
      const pid = Number(pool.id)
      if (Number.isNaN(pid)) return

      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)
      const userInfo = await contract.userInfo(pid, account)

      // 兼容数组/对象两种返回形式
      const amountBN = userInfo.amount ?? userInfo[0]
      const decimals = pool.stakeTokenDecimals || 18
      const amount = formatUnits(amountBN, decimals)

      setUserStakedAmount(amount)
    } catch (error) {
      console.error('Failed to load user staked amount:', error)
    }
  }

  useEffect(() => {
    loadUserStakedAmount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, library, pool.id, pool.stakeTokenDecimals])

  const handleEnable = async () => {
    if (isWMOVA || approvalState !== ApprovalState.NOT_APPROVED) return

    // 检查池子是否已开始
    const startTimeMs = pool.startTime && pool.startTime > 0 ? pool.startTime * 1000 : 0
    if (startTimeMs > Date.now()) {
      addPopup({ message: { type: 'error', text: t('yieldFarming.poolNotStarted') } })
      return
    }

    try {
      await approveCallback()
    } catch (error) {
      console.error('Failed to approve:', error)
      // 不是用户手动取消的情况，提示错误
      if ((error as any).code !== 4001) {
        addPopup({ message: { type: 'error', text: 'Network error' } })
      }
    }
  }

  const handleStake = async () => {
    if (!account || !library || !stakeAmount || parseFloat(stakeAmount) <= 0) return

    // 检查池子是否已开始
    const startTimeMs = pool.startTime && pool.startTime > 0 ? pool.startTime * 1000 : 0
    if (startTimeMs > Date.now()) {
      addPopup({ message: { type: 'error', text: t('yieldFarming.poolNotStarted') } })
      return
    }

    if (!pool.isLive) {
      addPopup({ message: { type: 'error', text: t('yieldFarming.poolNotLive') } })
      return
    }

    const pid = Number(pool.id)
    if (Number.isNaN(pid)) return

    const numericAmount = parseFloat(stakeAmount)
    if (numericAmount > walletBalanceNumber) {
      addPopup({ message: { type: 'error', text: t('yieldFarming.amountExceedsBalance') } })
      return
    }

    const maxStake = parseFloat(pool.maxStakePerUser || '0')
    const currentStakedNumeric = parseFloat(userStakedAmount || '0')
    if (maxStake > 0 && currentStakedNumeric + numericAmount > maxStake) {
      addPopup({ message: { type: 'error', text: t('yieldFarming.amountExceedsMaxStake') } })
      return
    }

    // 检查授权额度：如果不是原生代币，需要确保授权额度大于等于stake数量
    // 如果授权额度不足，自动执行授权
    if (!isWMOVA && stakingToken) {
      const decimals = pool.stakeTokenDecimals || 18
      const stakeAmountWei = parseUnits(stakeAmount, decimals)
      const stakeAmountToken = new TokenAmount(stakingToken, stakeAmountWei.toString())

      if (!currentAllowance || currentAllowance.lessThan(stakeAmountToken)) {
        // 授权额度不足，先执行授权
        if (approvalState === ApprovalState.PENDING) {
          // 如果正在授权中，提示用户等待
          addPopup({
            message: {
              type: 'info',
              text: t('yieldFarming.approving')
            }
          })
          return
        }

        // 设置 isStaking 为 true，使按钮显示"staking"状态且禁用
        setIsStaking(true)
        try {
          // 执行授权
          await approveCallback()
          // 授权成功后，等待一下让状态更新（授权状态和授权额度需要时间更新）
          // 由于授权的是 MaxUint256，授权成功后应该总是足够的
          // 但为了确保，我们等待一段时间让 hook 更新
          await new Promise(resolve => setTimeout(resolve, 2000))
          // 授权成功后，继续执行stake操作（因为授权的是 MaxUint256，应该总是足够的）
          // isStaking 保持为 true，继续执行下面的 stake 逻辑
        } catch (error) {
          console.error('Failed to approve:', error)
          // 授权失败，恢复按钮状态
          setIsStaking(false)
          // 不是用户手动取消的情况，提示错误
          if ((error as any).code !== 4001) {
            addPopup({ message: { type: 'error', text: 'Network error' } })
          }
          return
        }
      }
    }

    const decimals = pool.stakeTokenDecimals || 18
    const amountWei = parseUnits(stakeAmount, decimals)

    // 如果之前没有设置（即不需要授权的情况），现在设置
    if (!isStaking) {
      setIsStaking(true)
    }
    try {
      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)
      const overrides = isWMOVA ? { value: amountWei } : {}
      const tx = await contract.deposit(pid, amountWei, overrides)
      await tx.wait()

      await loadUserStakedAmount()
      setStakeAmount('')
      addPopup({ message: { type: 'success', text: t('yieldFarming.stakeSuccess') } })
      setShowStakeModal(false)
      // 刷新池子数据
      if (onPoolUpdate) {
        onPoolUpdate(pool.id)
      }
    } catch (error) {
      console.error('Failed to stake:', error)
      // 不是用户手动取消的情况，提示错误
      if ((error as any).code !== 4001) {
        addPopup({ message: { type: 'error', text: t('yieldFarming.stakeFailed') + ': ' + (error as any).message } })
      }
    } finally {
      setIsStaking(false)
    }
  }

  const handleUnstake = async () => {
    if (!account || !library || !unstakeAmount || parseFloat(unstakeAmount) <= 0) return

    const pid = Number(pool.id)
    if (Number.isNaN(pid)) return

    setIsUnstaking(true)
    try {
      const decimals = pool.stakeTokenDecimals || 18
      const amountWei = parseUnits(unstakeAmount, decimals)
      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)
      const tx = await contract.withdraw(pid, amountWei)
      await tx.wait()

      await loadUserStakedAmount()
      // 刷新批量待领取收益数据
      if (onRefreshEarned) {
        onRefreshEarned()
      }
      setUnstakeAmount('')
      addPopup({ message: { type: 'success', text: t('yieldFarming.unstakeSuccess') } })
      setShowUnstakeModal(false)
      // 刷新池子数据
      if (onPoolUpdate) {
        onPoolUpdate(pool.id)
      }
    } catch (error) {
      console.error('Failed to unstake:', error)
      // 不是用户手动取消的情况，提示错误
      if ((error as any).code !== 4001) {
        addPopup({ message: { type: 'error', text: t('yieldFarming.unstakeFailed') + ': ' + (error as any).message } })
      }
    } finally {
      setIsUnstaking(false)
    }
  }

  const handleUnstakeAll = async () => {
    if (!account || !library || !userStakedAmount || parseFloat(userStakedAmount) <= 0) return
    const pid = Number(pool.id)
    if (Number.isNaN(pid)) return

    setIsUnstaking(true)
    try {
      const decimals = pool.stakeTokenDecimals || 18
      const amountWei = parseUnits(userStakedAmount, decimals)
      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)
      const tx = await contract.withdraw(pid, amountWei)
      await tx.wait()

      await loadUserStakedAmount()
      // 刷新批量待领取收益数据
      if (onRefreshEarned) {
        onRefreshEarned()
      }
      addPopup({ message: { type: 'success', text: t('yieldFarming.unstakeSuccess') } })
      setShowUnstakeConfirm(false)
      // 刷新池子数据
      if (onPoolUpdate) {
        onPoolUpdate(pool.id)
      }
    } catch (error) {
      console.error('Failed to unstake all:', error)
      // 不是用户手动取消的情况，提示错误
      if ((error as any).code !== 4001) {
        addPopup({ message: { type: 'error', text: t('yieldFarming.unstakeFailed') + ': ' + (error as any).message } })
      }
    } finally {
      setIsUnstaking(false)
    }
  }

  // 数值化用户已质押数量
  const numericUserStaked = useMemo(() => {
    const v = parseFloat(userStakedAmount || '0')
    return Number.isFinite(v) ? v : 0
  }, [userStakedAmount])

  // 计算 USD 价值
  /* const stakedUSD = useMemo(() => {
    if (!pool.stakePrice || pool.stakePrice <= 0) return '--'
    const usdValue = numericUserStaked * pool.stakePrice
    return usdValue.toFixed(6)
  }, [numericUserStaked, pool.stakePrice]) */

  // 是否有质押金额，用于切换展示 StakedAmountContainer
  const hasStaked = numericUserStaked > 0

  // 检查池子是否已开始：startTime 是秒，需要转换为毫秒
  // 每次渲染时都检查，以确保实时更新
  const isNotStarted = pool.startTime && pool.startTime > 0 ? pool.startTime * 1000 > Date.now() : false

  const shouldShowUnstakeWarning = useMemo(() => {
    return !!pool.rewardForfeitedBeforeEnd
  }, [pool.rewardForfeitedBeforeEnd])

  const handleUnstakeButtonClick = () => {
    if (shouldShowUnstakeWarning && pool.isLive) {
      setShowUnstakeConfirm(true)
      return
    } else if (shouldShowUnstakeWarning && !pool.isLive) {
      handleUnstakeAll()
      return
    }

    setShowUnstakeModal(true)
  }

  const handleConfirmUnstakeWarning = () => {
    handleUnstakeAll()
    setShowUnstakeConfirm(false)
  }

  const handleCancelUnstakeWarning = () => {
    setShowUnstakeConfirm(false)
  }

  // 如果地址无效，显示错误信息
  if (!isValidContractAddress || !isValidStakingTokenAddress) {
    return (
      <div style={{ color: '#ff6b6b', fontSize: '0.9rem', textAlign: 'center', padding: '1rem' }}>
        {t('yieldFarming.invalidContractAddress')}
      </div>
    )
  }

  return (
    <>
      <SectionLabel>
        {!isApproved && pool.isLive
          ? t('yieldFarming.enablePool')
          : hasStaked
          ? t('yieldFarming.staked', { token: pool.stakeToken })
          : t('yieldFarming.stakeToken', { token: pool.stakeToken })}
      </SectionLabel>
      {!isApproved && pool.isLive ? (
        <EnableButton
          onClick={handleEnable}
          disabled={approvalState === ApprovalState.PENDING || isNotStarted || !pool.isLive || !account}
        >
          {approvalState === ApprovalState.PENDING
            ? t('yieldFarming.enabling')
            : isNotStarted
            ? t('yieldFarming.notStarted')
            : t('yieldFarming.enable')}
        </EnableButton>
      ) : hasStaked ? (
        <>
          <StakedAmountContainer>
            <StakedAmountWrapper>
              <StakedAmount>{userStakedAmount}</StakedAmount>
              {/* <StakedUSD>{stakedUSD} USD1</StakedUSD> */}
            </StakedAmountWrapper>
            <StakedButtons>
              {shouldShowUnstakeWarning ? (
                <UnstakeTextButton onClick={handleUnstakeButtonClick} disabled={isUnstaking || !account}>
                  {isUnstaking ? t('yieldFarming.unstaking') : t('yieldFarming.unstake')}
                </UnstakeTextButton>
              ) : (
                <StakedButton onClick={handleUnstakeButtonClick}>
                  <img src="/images/minus.png" alt="minus" />
                </StakedButton>
              )}
              <StakedButton onClick={() => setShowStakeModal(true)} disabled={!pool.isLive || isNotStarted || !account}>
                <img src="/images/plus_sign.png" alt="plus" />
              </StakedButton>
            </StakedButtons>
          </StakedAmountContainer>
          <StakeModal
            isOpen={showStakeModal}
            onDismiss={() => setShowStakeModal(false)}
            pool={pool}
            stakeAmount={stakeAmount}
            onStakeAmountChange={(value: string) => setStakeAmount(value)}
            onConfirm={handleStake}
            isStaking={isStaking}
            balance={walletBalance?.toExact() ?? '0'}
            maxStakePerUser={pool.maxStakePerUser}
            stakeTokenLogoURL={pool.stakeTokenLogoURL}
            stakePrice={pool.stakePrice}
          />
          <Modal isOpen={showUnstakeConfirm} onDismiss={handleCancelUnstakeWarning} maxHeight={90}>
            <WarningModalContent>
              <WarningHeader>
                <WarningCloseButton onClick={handleCancelUnstakeWarning} />
              </WarningHeader>
              <WarningContent>
                <WarningTitle>{t('yieldFarming.unstakeActiveTitle')}</WarningTitle>
                <WarningDescription>{t('yieldFarming.unstakeActiveDesc')}</WarningDescription>
              </WarningContent>
              <WarningActions>
                <WarningPrimaryButton onClick={handleConfirmUnstakeWarning}>
                  {isUnstaking ? t('yieldFarming.unstaking') : t('yieldFarming.unstakeActiveConfirm')}
                </WarningPrimaryButton>
                <WarningSecondaryButton onClick={handleCancelUnstakeWarning}>
                  {t('yieldFarming.unstakeActiveCancel')}
                </WarningSecondaryButton>
              </WarningActions>
            </WarningModalContent>
          </Modal>
          <UnstakeModal
            isOpen={showUnstakeModal}
            onDismiss={() => setShowUnstakeModal(false)}
            pool={pool}
            unstakeAmount={unstakeAmount}
            onUnstakeAmountChange={(value: string) => setUnstakeAmount(value)}
            onConfirm={handleUnstake}
            isUnstaking={isUnstaking}
            stakedBalance={userStakedAmount}
            balance={walletBalance?.toExact() ?? '0'}
            stakeTokenLogoURL={pool.stakeTokenLogoURL}
            stakePrice={pool.stakePrice}
            userStakedAmount={userStakedAmount}
          />
        </>
      ) : (
        <>
          <StakingButton onClick={() => setShowStakeModal(true)} disabled={isStaking || !pool.isLive || isNotStarted}>
            {isStaking
              ? t('yieldFarming.staking')
              : isNotStarted
              ? t('yieldFarming.notStarted')
              : t('yieldFarming.stake')}
          </StakingButton>
          <StakeModal
            isOpen={showStakeModal}
            onDismiss={() => setShowStakeModal(false)}
            pool={pool}
            stakeAmount={stakeAmount}
            onStakeAmountChange={(value: string) => setStakeAmount(value)}
            onConfirm={handleStake}
            isStaking={isStaking}
            balance={walletBalance?.toExact() ?? '0'}
            maxStakePerUser={pool.maxStakePerUser}
            stakeTokenLogoURL={pool.stakeTokenLogoURL}
            stakePrice={pool.stakePrice}
          />
        </>
      )}
    </>
  )
}

export default function YieldFarming() {
  const { t } = useTranslation()
  const { account, chainId, library } = useActiveWeb3React()
  const [activeTab, setActiveTab] = useState<'live' | 'old'>('live')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedPoolId, setExpandedPoolId] = useState<string | null>(null)
  const [pools, setPools] = useState<Pool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // 存储每个池子的待领取收益，key 为 pool.id，value 为格式化的收益字符串
  const [userEarnedMap, setUserEarnedMap] = useState<Map<string, string>>(new Map())

  // 批量获取所有池子的待领取收益
  const loadBatchPendingRewards = async () => {
    if (!account || !chainId || !library || pools.length === 0) {
      setUserEarnedMap(new Map())
      return
    }

    try {
      const contract = getContract(STAKE_CONTRACT_ADDRESS, (StakeABI as any).abi ?? StakeABI, library, account)

      // 收集所有有效的 pool id
      const pids: number[] = []
      const poolIdToIndexMap = new Map<string, number>()

      pools.forEach(pool => {
        const pid = Number(pool.id)
        if (!Number.isNaN(pid)) {
          poolIdToIndexMap.set(pool.id, pids.length)
          pids.push(pid)
        }
      })

      if (pids.length === 0) {
        setUserEarnedMap(new Map())
        return
      }

      // 批量调用 batchPendingReward
      const pendingRewards = await contract.batchPendingReward(pids, account)

      // 将结果映射回 pool.id
      const newEarnedMap = new Map<string, string>()
      pools.forEach(pool => {
        const index = poolIdToIndexMap.get(pool.id)
        if (index !== undefined && pendingRewards[index]) {
          const decimals = pool.rewardTokenDecimals || 18
          const amount = formatUnits(pendingRewards[index], decimals)
          newEarnedMap.set(pool.id, Number(amount).toFixed(6))
        } else {
          newEarnedMap.set(pool.id, '0')
        }
      })

      setUserEarnedMap(newEarnedMap)
    } catch (error) {
      console.error('Failed to load batch pending rewards:', error)
      // 出错时将所有池子的收益设为 '0'
      const errorMap = new Map<string, string>()
      pools.forEach(pool => {
        errorMap.set(pool.id, '0')
      })
      setUserEarnedMap(errorMap)
    }
  }

  // 获取池子数据 - 分两阶段加载：先快速显示基本数据，再异步更新价格和 APR
  useEffect(() => {
    async function loadPools() {
      setLoading(true)
      setError(null)
      try {
        // 第一阶段：快速获取基本数据并立即显示
        const basicPools = await fetchPoolsBasic()
        setPools(basicPools)
        setLoading(false) // 基本数据加载完成，可以显示列表了

        // 第二阶段：异步更新价格和 APR，不阻塞界面显示
        updatePoolsWithPricesAndAPR(basicPools)
          .then(updatedPools => {
            setPools(updatedPools)
          })
          .catch(err => {
            console.error('Error updating pools with prices and APR:', err)
            // 即使更新失败，基本数据仍然可以显示
          })
      } catch (err) {
        setError(err instanceof Error ? err.message : t('yieldFarming.failedToLoadPools'))
        console.error('Error loading pools:', err)
        setLoading(false)
      }
    }

    loadPools()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 当 account 或 pools 变化时，批量获取待领取收益
  // 使用 pools 的 id 列表作为依赖，避免不必要的重新获取
  const poolsIds = useMemo(() => pools.map(p => p.id).join(','), [pools])
  useEffect(() => {
    if (pools.length > 0) {
      loadBatchPendingRewards()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, chainId, library, poolsIds])

  // 过滤池子
  const filteredPools = useMemo(() => {
    let filtered = pools.filter(pool => pool.isLive === (activeTab === 'live'))

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        pool =>
          pool.name.toLowerCase().includes(query) ||
          pool.stakeToken.toLowerCase().includes(query) ||
          pool.rewardToken.toLowerCase().includes(query)
      )
    }

    // old 列表按 endTime 降序，相同 endTime 按 startTime 升序
    if (activeTab === 'old') {
      filtered = [...filtered].sort((a, b) => {
        if (b.endTime !== a.endTime) return b.endTime - a.endTime
        return a.startTime - b.startTime
      })
    }

    return filtered
  }, [pools, activeTab, searchQuery])

  const togglePool = (poolId: string) => {
    setExpandedPoolId(expandedPoolId === poolId ? null : poolId)
  }

  // 刷新池子列表数据
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePoolUpdate = async (_poolId: string) => {
    try {
      const fetchedPools = await fetchPools()
      setPools(fetchedPools)
    } catch (err) {
      console.error('Error refreshing pools:', err)
    }
  }

  return (
    <MainContainer>
      <ContentContainer>
        <AppBody style={{ background: 'transparent', boxShadow: 'none', maxWidth: '100%', minWidth: '82vw' }}>
          <HeaderSection>
            <Title>{t('yieldFarming.title')}</Title>
            <Description>{t('yieldFarming.description')}</Description>
          </HeaderSection>

          <FilterSection>
            <TabContainer>
              <TabButton active={String(activeTab) === 'live'} onClick={() => setActiveTab('live')}>
                {t('yieldFarming.live')}
              </TabButton>
              <TabButton active={String(activeTab) === 'old'} onClick={() => setActiveTab('old')}>
                {t('yieldFarming.old')}
              </TabButton>
            </TabContainer>

            <SearchContainer>
              <SearchLabel>{t('yieldFarming.search')}</SearchLabel>
              <SearchInput
                type="text"
                placeholder={t('yieldFarming.searchPools')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </FilterSection>

          <PoolList>
            {loading ? (
              <div style={{ color: '#ffffff', textAlign: 'center', padding: '2rem' }}>
                {t('yieldFarming.loadingPools')}
              </div>
            ) : error ? (
              <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '2rem' }}>
                {t('yieldFarming.error')}: {error}
              </div>
            ) : filteredPools.length > 0 ? (
              filteredPools.map(pool => (
                <PoolCardComponent
                  key={pool.id}
                  pool={pool}
                  expanded={expandedPoolId === pool.id}
                  onToggle={() => togglePool(pool.id)}
                  onPoolUpdate={handlePoolUpdate}
                  userEarned={userEarnedMap.get(pool.id) || '0'}
                  onRefreshEarned={loadBatchPendingRewards}
                />
              ))
            ) : (
              <div style={{ color: '#ffffff', textAlign: 'center', padding: '2rem' }}>
                {t('yieldFarming.noPoolsFound')}
              </div>
            )}
          </PoolList>
        </AppBody>
      </ContentContainer>
    </MainContainer>
  )
}
